// Ertragsausfall-Block im Risiken-Reiter (Meeting 24.07.2026, fünfter Bereich:
// „Ertragsausfall müssen wir da hin machen … das kann ich von den Kennzahlen
// runterziehen").
//
// ZEIGT NUR AN, ERFASST NICHT. Der Reiter „Risiken" führt die analysierten
// Risiken zusammen; erhoben werden sie im Fragebogen und in den Kennzahlen.
// Deshalb stehen hier zwei Kennzahlen und kein Eingabefeld:
//
//   1. Rohertrag — aus den Kennzahlen (risk-kpi/:contactId, Feld grossProfit);
//      die fachlich übliche Versicherungssumme für Ertragsausfall/BU.
//   2. Haftzeit — die vereinbarte Haftzeit des BU-Vertrags, gespeichert unter
//      contact/:id/ertragsausfall.
//
// Die Ampel darunter vergleicht die Haftzeit mit der Wiederaufnahme-Dauer aus
// dem Fragebogen: ist sie kürzer, deckt der Vertrag den Stillstand nicht ab.
// Beide gespeicherten Werte werden weiterhin gelesen — der POST-Endpunkt bleibt
// bestehen, nur hat er in diesem Reiter keinen Aufrufer mehr.

import * as React from "react";
import { useEffect, useState } from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { apiFetch } from "../../utils/apiFetch";
import { BRAND } from "../risikoanalyseStep/tokens";

/** Eine Antwort des Ertragsausfall-Fragebogens. */
type Antwort = { yn?: "ja" | "nein" | ""; note?: string };
type Antworten = Record<string, Antwort>;

interface KpiJahr {
  jahr?: number;
  year?: number;
  daten?: Record<string, unknown> | null;
  [k: string]: unknown;
}

const eur = (v: number) => `${Math.round(v).toLocaleString("de-DE")} €`;

/** Zahl aus einem Kennzahlen-Feld: kommt je nach Erfassung als Zahl oder String. */
const num = (v: unknown): number | null => {
  if (v == null || v === "") return null;
  const n = Number(String(v).replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : null;
};

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", rowGap: "14px" },
  kpis: {
    display: "grid",
    gap: "10px",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  },
  kpi: {
    display: "flex",
    flexDirection: "column",
    rowGap: "3px",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderLeft("3px", "solid", BRAND.gold),
    ...shorthands.borderRadius("10px"),
  },
  kpiLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  kpiValue: { fontSize: "1.05rem", fontWeight: 700, color: BRAND.ink },
  kpiHint: { fontSize: "0.72rem", color: BRAND.muted },
  warn: { color: "#c23b3b" },


});

export function ErtragsausfallSection({
  contactId,
  onBefund,
}: {
  contactId: number;
  /** Meldet nach oben, ob überhaupt etwas erfasst ist (Startzustand des Abschnitts). */
  onBefund?: (erfasst: boolean) => void;
}) {
  const styles = useStyles();

  const [haftzeit, setHaftzeit] = useState<string>("");
  const [wiederaufnahme, setWiederaufnahme] = useState<string>("");
  const [rohertrag, setRohertrag] = useState<{ wert: number; jahr: number | null } | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{
      answers: Antworten;
      haftzeitMonate: number | null;
      updatedAt: string | null;
    }>(`contact/${contactId}/ertragsausfall`)
      .then(({ data }) => {
        if (cancelled || !data) return;
        const a = data.answers ?? {};
        setHaftzeit(data.haftzeitMonate != null ? String(data.haftzeitMonate) : "");
        // Die Wiederaufnahme-Dauer („Wie lange benötigen Sie, um Ihren Betrieb
        // nach einem Schadenfall wieder fortsetzen zu können?") steht als Notiz
        // an dieser Antwort — sie ist der Maßstab, an dem die vereinbarte
        // Haftzeit gemessen wird.
        const wa = (a.wiederaufnahme_dauer as Antwort | undefined)?.note ?? "";
        setWiederaufnahme(String(wa));
      })
      .catch(() => {
        /* leerer Block ist ein gültiger Startzustand */
      });
    return () => {
      cancelled = true;
    };
  }, [contactId]);

  // Rohertrag = fachlich übliche Versicherungssumme für Ertragsausfall/BU.
  // Jüngstes Jahr mit einem Wert gewinnt; ohne Kennzahlen bleibt der Block leer
  // statt eine 0 zu behaupten.
  useEffect(() => {
    let cancelled = false;
    apiFetch<KpiJahr[]>(`risk-kpi/${contactId}`)
      .then(({ data }) => {
        if (cancelled || !Array.isArray(data)) return;
        const kandidaten = data
          .map((row) => {
            const jahr = Number(row.jahr ?? row.year);
            const daten = (row.daten ?? row) as Record<string, unknown>;
            return { jahr: Number.isFinite(jahr) ? jahr : null, wert: num(daten.grossProfit) };
          })
          .filter((k): k is { jahr: number | null; wert: number } => k.wert != null)
          .sort((a, b) => (b.jahr ?? 0) - (a.jahr ?? 0));
        setRohertrag(kandidaten[0] ?? null);
      })
      .catch(() => {
        if (!cancelled) setRohertrag(null);
      });
    return () => {
      cancelled = true;
    };
  }, [contactId]);

  const haftzeitNum = num(haftzeit);
  const wiederaufnahmeNum = num(wiederaufnahme);
  const haftzeitKnapp =
    haftzeitNum != null && wiederaufnahmeNum != null && haftzeitNum < wiederaufnahmeNum;

  // Gefüllt heißt: eine der beiden Zahlen steht. Alles andere ist ein leerer
  // Abschnitt, der im Reiter zugeklappt startet.
  useEffect(() => {
    onBefund?.(rohertrag != null || haftzeitNum != null);
  }, [rohertrag, haftzeitNum, onBefund]);

  return (
    <div className={styles.root}>
      <div className={styles.kpis}>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Rohertrag</span>
          <span className={styles.kpiValue}>{rohertrag ? eur(rohertrag.wert) : "—"}</span>
          <span className={styles.kpiHint}>
            {rohertrag
              ? `aus den Kennzahlen${rohertrag.jahr ? ` ${rohertrag.jahr}` : ""} — Basis der Versicherungssumme`
              : "keine Kennzahlen erfasst"}
          </span>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Haftzeit</span>
          <span className={styles.kpiValue}>
            {haftzeitNum != null ? `${haftzeitNum} Monate` : "—"}
          </span>
          <span className={mergeClasses(styles.kpiHint, haftzeitKnapp && styles.warn)}>
            {haftzeitKnapp
              ? `zu kurz — Wiederaufnahme dauert ${wiederaufnahmeNum} Monate`
              : haftzeitNum != null && wiederaufnahmeNum != null
              ? "deckt die Wiederaufnahme ab"
              : "vereinbarte Haftzeit des BU-Vertrags"}
          </span>
        </div>
      </div>

    </div>
  );
}
